import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';

const PrivateNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  

  // Scroll efekti
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Sayfa dışında bir yere tıklayınca user menüsünü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      const userMenu = document.getElementById('user-menu-container');
      if (userMenu && !userMenu.contains(event.target) && userMenuOpen) {
        setUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);
  
  // Aktif sayfa kontrolü
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  // Çıkış işlemi
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  // Navbar yüksekliği (stil/animasyon için)
  const navbarHeight = scrolled ? '70px' : '80px';
  
  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        height: navbarHeight,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
      }}
    >
      {/* Navbar arka planı (gradient, border ve blur efekti) */}
      <div 
        className={`absolute inset-0 transition-all duration-500 border-b ${
          scrolled 
            ? 'bg-white border-gray-300 shadow-md' 
            : 'bg-white border-gray-300 shadow-md'
        }`}
      >
        {/* Ekstra dekoratif öğeler */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
        
        {/* Parıltı efekti */}
        <div className={`absolute inset-0 bg-gradient-to-b from-white/5 to-transparent ${scrolled ? 'opacity-0' : 'opacity-50'}`}></div>
      </div>
      
      <div className="container mx-auto h-full px-6 relative z-10">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <Link to="/" className="group flex items-center">
            <div className="flex items-center bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl px-4 py-2 shadow-md transition-all duration-300 border border-purple-300 group-hover:shadow-purple-500/30">
                <span className="text-3xl mr-3 text-white group-hover:animate-pulse">♪</span>
                <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">
                İYTE Müzik
                </span>
            </div>
        </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/dashboard" isActive={isActive('/dashboard')}>Dashboard</NavLink>
            <NavLink to="/reserve" isActive={isActive('/reserve')}>Rezervasyon</NavLink>
            <NavLink to="/events" isActive={isActive('/events')}>Etkinlikler</NavLink>
            <NavLink to="/feedback" isActive={isActive('/feedback')}>
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                Destek
              </span>
            </NavLink>
            {user && user.admin && (
              <NavLink to="/admin" isActive={isActive('/admin')}>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Admin Panel
                </span>
              </NavLink>
            )}
            
            {/* User Menu */}
            <div className="relative ml-6" id="user-menu-container">
              <button 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center text-gray-700 focus:outline-none group hover:opacity-90 transition-all ml-4 bg-white hover:bg-gray-50 rounded-full px-4 py-2 border border-gray-300 hover:border-purple-300 shadow-sm"
              >
                <span className="text-sm mr-2 text-gray-700 max-w-[200px] truncate">{user?.email || 'Kullanıcı'}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-4 w-4 text-gray-500 transition-transform duration-300 ${userMenuOpen ? 'rotate-180' : ''}`} 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              <div 
                className={`absolute right-0 mt-2 w-64 rounded-xl overflow-hidden transition-all duration-300 transform origin-top-right ${
                  userMenuOpen 
                    ? 'opacity-100 scale-100 translate-y-0' 
                    : 'opacity-0 scale-95 -translate-y-3 pointer-events-none'
                }`}
                style={{ boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3), 0 5px 15px rgba(0, 0, 0, 0.2)' }}
              >
                {/* Dropdown kart arka planı */}
                <div className="bg-gradient-to-br from-white to-indigo-50 overflow-hidden">
                  {/* Kullanıcı bilgileri header */}
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-4">
                    <div>
                      <p className="text-indigo-200 text-sm">{user?.email || ''}</p>
                    </div>
                  </div>
                  
                  {/* Menü öğeleri */}
                  <div className="py-2">
                    
                    <button 
                      onClick={handleLogout}
                      className="w-full text-left px-6 py-3 text-red-600 hover:bg-red-50 transition-colors duration-200"
                    >
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Çıkış Yap</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`text-gray-700 focus:outline-none p-2 rounded-lg ${
                isOpen 
                  ? 'bg-purple-100' 
                  : 'bg-transparent hover:bg-gray-100'
              } transition-all duration-300`}
              aria-label="Toggle menu"
            >
              {!isOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      <div 
        className={`md:hidden fixed left-0 right-0 transition-all duration-300 ${
          isOpen 
            ? 'opacity-100 visible top-[80px]' 
            : 'opacity-0 invisible top-[60px]'
        }`}
        style={{ 
          boxShadow: isOpen ? '0 15px 35px rgba(0, 0, 0, 0.2)' : 'none',
          maxHeight: isOpen ? '100vh' : '0',
          overflow: 'hidden'
        }}
      >
        <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 backdrop-blur-lg border-t border-white/10 px-6 pb-6">
          {/* User Info */}
          <div className="py-6 border-b border-white/10">
            <div className="text-white/80 text-sm">{user?.email || ''}</div>
          </div>
          
          {/* Navigation Links */}
          <div className="py-4 space-y-1">
            <MobileNavLink to="/dashboard" isActive={isActive('/dashboard')} onClick={() => setIsOpen(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Dashboard
            </MobileNavLink>
            
            <MobileNavLink to="/reserve" isActive={isActive('/reserve')} onClick={() => setIsOpen(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Rezervasyon
            </MobileNavLink>
            
            <MobileNavLink to="/calendar" isActive={isActive('/calendar')} onClick={() => setIsOpen(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Takvim
            </MobileNavLink>
            
            <MobileNavLink to="/events" isActive={isActive('/events')} onClick={() => setIsOpen(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Etkinlikler
            </MobileNavLink>
            
            <MobileNavLink to="/feedback" isActive={isActive('/feedback')} onClick={() => setIsOpen(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              Destek
            </MobileNavLink>
            {user && user.admin && (
              <MobileNavLink to="/admin" isActive={isActive('/admin')} onClick={() => setIsOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Admin Panel
              </MobileNavLink>
            )}

          </div>
          
          {/* Logout Button */}
          <button 
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            className="flex w-full items-center py-3 px-4 mt-4 rounded-lg border border-white/10 bg-white/10 text-red-100 transition-colors duration-300 hover:bg-red-500/20 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Çıkış Yap</span>
          </button>
        </div>
      </div>
      
      {/* Stil Tanımları */}
      <style jsx>{`
        .shadow-text {
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </nav>
  );
};

// Desktop Nav Link
const NavLink = ({ to, isActive, children }) => {
  return (
    <Link 
      to={to} 
      className={`px-4 py-2 rounded-lg transition-all duration-300 relative group ${
        isActive 
          ? 'bg-purple-100 text-purple-700 font-medium' 
          : 'text-gray-700 hover:text-purple-700 hover:bg-purple-50'
      }`}
    >
      {/* Text without shadow for better readability */}
      <span className="relative z-10 text-sm uppercase tracking-wider font-medium">{children}</span>
      
      {/* Active indicator line */}
      <span 
        className={`absolute bottom-0 left-0 right-0 mx-auto w-12 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 transform transition-all duration-300 ${
          isActive 
            ? 'opacity-100 scale-x-100' 
            : 'opacity-0 scale-x-0 group-hover:opacity-70 group-hover:scale-x-75'
        }`}
      ></span>
    </Link>
  );
};

// Mobile Nav Link
const MobileNavLink = ({ to, isActive, onClick, children }) => {
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`flex items-center py-3 px-4 rounded-lg transition-all duration-300 ${
        isActive 
          ? 'bg-white text-indigo-900 font-semibold shadow-sm' 
          : 'text-indigo-50 hover:bg-white/10 hover:text-white'
      }`}
    >
      {children}
    </Link>
  );
};

export default PrivateNavbar;